import React from "react";
import { useSignedUrl } from "@/hooks/useSignedUrl";
import { openStorageFile, type ParseOptions } from "@/utils/storageRefs";

type ImgProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** A stored file value: canonical ref, legacy public URL or any URL. */
  src: string | null | undefined;
  defaultBucket?: ParseOptions["defaultBucket"];
};

/** An <img> for a stored file: renders once its signed link has arrived. */
export const StorageImage: React.FC<ImgProps> = ({ src, defaultBucket, alt, ...rest }) => {
  const { url } = useSignedUrl(src, { defaultBucket });
  if (!url) return null;
  return <img src={url} alt={alt} {...rest} />;
};

type LinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick"> & {
  href: string | null | undefined;
  defaultBucket?: ParseOptions["defaultBucket"];
};

/**
 * An <a> for a stored file. The stored value is not an openable URL any more,
 * so the click asks for a signed link and opens that in a new tab.
 */
export const StorageLink: React.FC<LinkProps> = ({ href, defaultBucket, children, ...rest }) => (
  <a
    href="#"
    rel="noopener noreferrer"
    {...rest}
    onClick={(e) => {
      e.preventDefault();
      void openStorageFile(href, { defaultBucket });
    }}
  >
    {children}
  </a>
);
